"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "@untitledui/icons";
import type { FC, ReactNode, Ref, RefAttributes } from "react";
import { isValidElement } from "react";
import type { SelectProps as AriaSelectProps } from "react-aria-components";
import { Button as AriaButton, ListBox as AriaListBox, Select as AriaSelect, SelectValue as AriaSelectValue } from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { ComboBox } from "./combobox";
import { Popover } from "./popover";
import { SelectItem } from "./select-item";
import { type CommonProps, SelectContext, type SelectItemType, sizes } from "./select-shared";
export type { CommonProps, SelectItemType };
export { SelectContext, sizes };
interface SelectProps extends Omit<AriaSelectProps<SelectItemType>, "children" | "items">, RefAttributes<HTMLDivElement>, CommonProps {
  items?: SelectItemType[];
  popoverClassName?: string;
  UNSTABLE_portalContainer?: Element | null;
  placeholderIcon?: FC | ReactNode;
  valueTextClassName?: string;
  children: ReactNode | ((item: SelectItemType) => ReactNode);
}
interface SelectValueProps {
  isOpen: boolean;
  size: "sm" | "md" | "lg";
  isFocused: boolean;
  isDisabled: boolean;
  placeholder?: string;
  ref?: Ref<HTMLButtonElement>;
  placeholderIcon?: FC | ReactNode;
  valueTextClassName?: string;
}
const SelectValue = t0 => {
  const $ = _c(18);
  const {
    isOpen,
    isFocused,
    isDisabled,
    size,
    placeholder,
    placeholderIcon,
    valueTextClassName,
    ref
  } = t0;
  const t1 = (isFocused || isOpen) && "ring-2 ring-brand";
  const t2 = isDisabled && "cursor-not-allowed bg-disabled_subtle text-disabled";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = sizes[size];
  let t5;
  if ($[3] !== t4.root) {
    t5 = cx("flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle", "*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled", t4.root);
    $[3] = t4.root;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isDisabled || $[6] !== placeholder || $[7] !== placeholderIcon || $[8] !== size || $[9] !== valueTextClassName) {
    t6 = state => {
      const Icon = state.selectedItem?.icon || placeholderIcon;
      return <>{state.selectedItem?.avatarUrl ? <Avatar size="xs" src={state.selectedItem.avatarUrl} alt={state.selectedItem.label} /> : isReactComponent(Icon) ? <Icon data-icon={true} aria-hidden="true" /> : isValidElement(Icon) ? Icon : null}{state.selectedItem ? <section className="flex w-full gap-2 truncate"><p className={cx("truncate text-md font-normal text-primary", valueTextClassName)}>{state.selectedItem?.label}</p>{state.selectedItem?.supportingText && <p className="text-md text-tertiary">{state.selectedItem?.supportingText}</p>}</section> : <p className={cx("text-md text-placeholder", isDisabled && "text-disabled", valueTextClassName)}>{placeholder}</p>}<ChevronDown aria-hidden="true" className={cx("ml-auto shrink-0 text-fg-quaternary", size === "sm" ? "size-4 stroke-[2.5px]" : "size-5")} /></>;
    };
    $[5] = isDisabled;
    $[6] = placeholder;
    $[7] = placeholderIcon;
    $[8] = size;
    $[9] = valueTextClassName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <AriaSelectValue className={t5}>{t6}</AriaSelectValue>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== ref || $[15] !== t3 || $[16] !== t7) {
    t8 = <AriaButton ref={ref} className={t3}>{t7}</AriaButton>;
    $[14] = ref;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
const Select = t0 => {
  const $ = _c(37);
  let UNSTABLE_portalContainer;
  let children;
  let className;
  let hint;
  let items;
  let label;
  let placeholderIcon;
  let popoverClassName;
  let rest;
  let t1;
  let t2;
  let tooltip;
  let valueTextClassName;
  if ($[0] !== t0) {
    ({
      placeholder: t1,
      placeholderIcon,
      size: t2,
      children,
      items,
      label,
      hint,
      tooltip,
      className,
      valueTextClassName,
      popoverClassName,
      UNSTABLE_portalContainer,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = UNSTABLE_portalContainer;
    $[2] = children;
    $[3] = className;
    $[4] = hint;
    $[5] = items;
    $[6] = label;
    $[7] = placeholderIcon;
    $[8] = popoverClassName;
    $[9] = rest;
    $[10] = t1;
    $[11] = t2;
    $[12] = tooltip;
    $[13] = valueTextClassName;
  } else {
    UNSTABLE_portalContainer = $[1];
    children = $[2];
    className = $[3];
    hint = $[4];
    items = $[5];
    label = $[6];
    placeholderIcon = $[7];
    popoverClassName = $[8];
    rest = $[9];
    t1 = $[10];
    t2 = $[11];
    tooltip = $[12];
    valueTextClassName = $[13];
  }
  const placeholder = t1 === undefined ? "Select" : t1;
  const size = t2 === undefined ? "sm" : t2;
  let t3;
  if ($[14] !== size) {
    t3 = {
      size
    };
    $[14] = size;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== className) {
    t4 = state => cx("flex flex-col gap-1.5", typeof className === "function" ? className(state) : className);
    $[16] = className;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== UNSTABLE_portalContainer || $[19] !== children || $[20] !== hint || $[21] !== items || $[22] !== label || $[23] !== placeholder || $[24] !== placeholderIcon || $[25] !== popoverClassName || $[26] !== size || $[27] !== tooltip || $[28] !== valueTextClassName) {
    t5 = state_0 => <>{label && <Label isRequired={state_0.isRequired} tooltip={tooltip}>{label}</Label>}<SelectValue {...state_0} {...{
        size,
        placeholder,
        valueTextClassName
      }} placeholderIcon={placeholderIcon} /><Popover size={size} className={popoverClassName} UNSTABLE_portalContainer={UNSTABLE_portalContainer ?? undefined}><AriaListBox items={items} className="size-full outline-hidden">{children}</AriaListBox></Popover>{hint && <HintText isInvalid={state_0.isInvalid}>{hint}</HintText>}</>;
    $[18] = UNSTABLE_portalContainer;
    $[19] = children;
    $[20] = hint;
    $[21] = items;
    $[22] = label;
    $[23] = placeholder;
    $[24] = placeholderIcon;
    $[25] = popoverClassName;
    $[26] = size;
    $[27] = tooltip;
    $[28] = valueTextClassName;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== rest || $[31] !== t4 || $[32] !== t5) {
    t6 = <AriaSelect {...rest} className={t4}>{t5}</AriaSelect>;
    $[30] = rest;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
  } else {
    t6 = $[33];
  }
  let t7;
  if ($[34] !== t3 || $[35] !== t6) {
    t7 = <SelectContext.Provider value={t3}>{t6}</SelectContext.Provider>;
    $[34] = t3;
    $[35] = t6;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  return t7;
};
const _Select = Select as typeof Select & {
  ComboBox: typeof ComboBox;
  Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;
export { _Select as Select };